import { useCardStore } from '@/stores/useCardStore';
import { MenuItem } from '@/types/card';
import { cn } from '@/utils/cn';
import { motion } from 'framer-motion';
import React from 'react';

interface UpdateCardMenuProps {
  id?: string;
  menuItems: MenuItem[];
  isCollapsed?: boolean;
}

export const UpdateCardMenu = ({ id, menuItems }: UpdateCardMenuProps) => {
  const { selectedMenuItem, setSelectedMenuItem } = useCardStore();

  const handleItemClick = (itemId: number) => {
    setSelectedMenuItem(itemId);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 400,
        damping: 20,
      },
    },
  };

  return (
    <motion.div
      id={id}
      className="flex flex-col items-center gap-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {menuItems.map(item => {
        const isActive = selectedMenuItem === item.id;
        return (
          <motion.button
            key={item.id}
            id={`menu-item-${item.id}`}
            onClick={() => handleItemClick(item.id)}
            className={cn(
              'group relative flex size-11 items-center justify-center rounded-xl transition-all duration-200',
              isActive ? '' : 'text-gray-500',
            )}
            title={typeof item.title === 'string' ? item.title : ''}
            variants={itemVariants}
            whileHover={{
              scale: 1.05,
              transition: {
                type: 'spring' as const,
                stiffness: 400,
                damping: 10,
              },
            }}
            whileTap={{
              scale: 0.95,
            }}
          >
            {React.isValidElement(item.title) &&
              item.title.props.children.map(
                (child: React.ReactElement, index: number) => {
                  if (index === 0) {
                    return React.cloneElement(child, {
                      key: `icon-${item.id}-${index}`,
                      className: cn(
                        child.props.className,
                        'text-xl transition-all duration-300',
                        isActive ? 'text-linko-light' : 'text-gray-600',
                        'group-hover:scale-110 group-hover:text-linko-light',
                      ),
                    });
                  }
                  return null;
                },
              )}
          </motion.button>
        );
      })}
    </motion.div>
  );
};
